import React, { PropsWithChildren, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useSubscriptionRequests } from "../../../api/grpc/subscription/useSubscriptionRequests";
import { EntityType } from "../../../api/grpc/subscription/subscription";
import { useCustomerRequests } from "../../../api/grpc/customer/useCustomerRequests";
import { useCheckUserPermissions } from "../../hooks/useCheckUserPermissions";

import { SubscriptionContext } from "./SubscriptionContext";
import { UserPermissions } from "../../../api/grpc/permissions/permissions";
import { hasUnlimitedUsersAddon } from "../../helpers/hasUnlimitedUsersAddon";

export const SubscriptionContextProvider = (props: PropsWithChildren<{}>) => {
  const [isChannelPartnerAccount, setIsChannelPartnerAccount] = useState(false);
  const [hasUnlimitedUsers, setHasUnlimitedUsers] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const { getChildAccounts, getSubscription } = useSubscriptionRequests();
  const { getCustomerSettings, updateCustomerSettings } =
    useCustomerRequests();
  const { checkUserPermission } = useCheckUserPermissions();

  const loadChannelPartner = async () => {
    try {
      const { response } = await getChildAccounts();

      setIsChannelPartnerAccount(
        response.customerIds.length > 0 &&
          checkUserPermission(UserPermissions.ORG_SETTINGS_SUBS_VIEW_EDIT)
      );
    } catch (error: any) {
      toast.error(error?.message || "Error loading channel partner accounts");
    }
  };

  // Loads subscription-level add-on state and keeps the cross-organization
  // customer setting synchronized with Chargebee.
  const loadSubscriptionAddons = async () => {
    try {
      const { response: subscription } = await getSubscription();

      const hasCrossOrgCalendarAddon = subscription.addons.some(
        (addon) => addon.entityType === EntityType.CROSS_ORG_ROOM_CALENDARS
      );
      setHasUnlimitedUsers(hasUnlimitedUsersAddon(subscription.addons));

      const { response: customerSettings } = await getCustomerSettings();

      if (
        customerSettings.crossOrgRoomCalendarEnabled === hasCrossOrgCalendarAddon
      ) {
        return;
      }

      await updateCustomerSettings({
        startTime: customerSettings.startTime,
        endTime: customerSettings.endTime,
        errorReportingEmail: customerSettings.errorReportingEmail,
        meetingSpaceCostPerHr: customerSettings.meetingSpaceCostPerHr,
        currency: customerSettings.currency,
        workingWeekDays: customerSettings.workingWeekDays,
        collectInsights: customerSettings.collectInsigths,
        ssoEnable: customerSettings.ssoEnable,
        disableDeskSimultaneous: customerSettings.disableDeskSimultaneous,
        showEventOrganizer: customerSettings.showEventOrganizer,
        deskBookingDisabledForRoles: customerSettings.deskBookingDisabledForRoles,
        deskBookingEnabled: customerSettings.deskBookingEnabled,
        roomBookingDisabledForRoles: customerSettings.roomBookingDisabledForRoles,
        roomBookingEnabled: customerSettings.roomBookingEnabled,
        systemNotificationRecipientUserIds:
          customerSettings.systemNotificationRecipientUserIds,
        crossOrgRoomCalendarEnabled: hasCrossOrgCalendarAddon,
      });
    } catch (error: any) {
      console.error(error);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  useEffect(() => {
    loadChannelPartner();
    loadSubscriptionAddons();
  }, []);

  const context: SubscriptionContext = {
    isChannelPartnerAccount,
    hasUnlimitedUsers,
    subscriptionLoading,
  };

  return (
    <SubscriptionContext.Provider value={context}>
      {props.children}
    </SubscriptionContext.Provider>
  );
};
